import {
  BestSeller,
  CategoryNames,
  Media,
  Product,
  SPECIES_NAME_LARGEMOUTH,
  StateDetail,
  StateHotbait,
  StateTopWaterbody,
} from '@omniafishing/core';
import { Affix } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { isPending, LoadingState } from '../../constants/loading_state';
import { useQueryString } from '../../hooks/use_query_string';
import { useReferenceData } from '../../hooks/use_reference_data';
import { useResponsive } from '../../hooks/use_responsive';
import { apiV1 } from '../../lib/api';
import { OmniaQueryParams } from '../../lib/query_string';
import { scrollToElementTop } from '../../lib/scroll';
import { WebAnalytics } from '../../lib/web_analytics';
import { PrismicDocument, PrismicQa } from '../../prismic';
import { getTechniqueByTechniqueName, SEASON_GROUP_NAME_ICE } from '../../redux/reference_data';
import { kitCategoryNames } from '../../routes/get_started/kit_inline/kit_inline';
import OmniaCarousel from '../carousel/omnia_carousel';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import Loading from '../loading/loading';
import { MediaCardLegacy } from '../media_card_legacy/media_card_legacy';
import { QuestionsCollapse } from '../questions_collapse/questions_collapse';
import { SeasonSpeciesHeaderTag } from '../season_species_header_tag/season_species_header_tag';
import { SeasonSpeciesSelects } from '../season_species_selects/season_species_selects';
import { SEO } from '../seo/seo';
import { Spacer } from '../spacer/spacer';
import { TechniqueOverflowMenu } from '../technique_overflow_menu/technique_overflow_menu';
import { speciesNotAllowedInIceSeason } from '../waterbody_detail/waterbody_detail_lib/determine_species';
import { WATERBODY_DETAIL_NAV_HEIGHT_APPROX } from '../waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_nav';
import { TechniqueFullWithReportCount } from '../waterbody_detail/waterbody_detail_sections/waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import { CategoryProductsCarousels } from './category_products_carousels';
import { FishingReportButton } from './fishing_report_button';
import styles from './state_detail.less';
import { StateDetailNav } from './state_detail_nav';
import { StateStats } from './state_stats';
import { TopWaterbodies } from './top_waterbodies';

// ArticleCard, OmniaCarousel, CarouselProductCard, Loading
// cause the CSS order warning in webpack

export enum StatePageIds {
  overview = 'overview',
  top_lakes = 'top_lakes',
  popular = 'popular',
  videos_articles = 'videos_articles',
  questions = 'questions',
  stats = 'stats',
}
export interface StateDetailProps {
  stateDetail: StateDetail;
  media: Media[];
  qas: PrismicDocument<PrismicQa>[];
}

export const StateDetailComponent = (props: StateDetailProps) => {
  const { stateDetail, media, qas } = props;

  const { abbr, bbox, meta_description, meta_title, name, season_groups, slug } = stateDetail;

  const { isDesktop, isMobile } = useResponsive();
  const { getCurrentQuery, replaceQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<{
    [OmniaQueryParams.technique]: string;
    [OmniaQueryParams.season_group]: string;
    [OmniaQueryParams.species]: string;
  }>();
  const { allSeasonGroups, allTechniques } = useReferenceData();
  const techniqueByTechniqueName = useSelector(getTechniqueByTechniqueName);

  const productsRef = useRef<HTMLDivElement>();
  const location = useLocation();

  const queryParamTechniqueName = currentQuery.technique;
  const [selectedTechniqueName, setSelectedTechniqueName] = useState(queryParamTechniqueName);
  const [topTechniquesNamesCounts, setTopTechniqueNamesCounts] = useState(
    {} as Record<string, number>
  );

  const topTechniquesWithCounts = Object.entries(topTechniquesNamesCounts).map(([key, value]) => {
    const technique = allTechniques.find((t) => t.name === key);
    const techniqueWithCount = { ...technique, reportCount: value };
    return techniqueWithCount as TechniqueFullWithReportCount;
  });

  const orderedTopTechniquesWithCounts = _.orderBy(
    topTechniquesWithCounts,
    ['reportCount', 'display_name'],
    ['desc', 'asc']
  );

  const currentSeasonGroup = season_groups.find((sg) => sg.active);
  const determinedSeasonGroupName = currentQuery.season_group || currentSeasonGroup.name;
  const [selectedSeasonGroupName, setSelectedSeasonGroupName] = useState(determinedSeasonGroupName);
  const selectedSeasonGroup = allSeasonGroups.find((sg) => sg.name === selectedSeasonGroupName);
  const stateSpecies = stateDetail.species;
  const isIceSeason = selectedSeasonGroup.name === SEASON_GROUP_NAME_ICE;
  const iceFilteredSpecies = stateSpecies.filter(
    (s) => !speciesNotAllowedInIceSeason.includes(s.name)
  );

  const stateContainsLargemouth = stateSpecies.some((s) => s.name === SPECIES_NAME_LARGEMOUTH);
  const stateContainsWalleye = stateSpecies.some((s) => s.name === 'walleye');
  const determinedSpeciesName =
    currentQuery.species ||
    (isIceSeason
      ? stateContainsWalleye
        ? 'walleye'
        : iceFilteredSpecies[0].name
      : stateContainsLargemouth
      ? SPECIES_NAME_LARGEMOUTH
      : stateSpecies[0].name);
  const [selectedSpeciesName, setSelectedSpeciesName] = useState(determinedSpeciesName);
  const selectedSpecies = stateSpecies.find((s) => s.name === selectedSpeciesName);

  const [seasonSpeciesTopWaterbodies, setSeasonSpeciesTopWaterbodies] = useState<
    StateTopWaterbody[]
  >([]);

  const [fishingReportsCount, setFishingReportsCount] = useState<number>();
  const [fishingReports30DaysCount, setFishingReports30DaysCount] = useState<number>();

  useEffect(() => {
    WebAnalytics.fishingIntentChange({
      stateAbbreviation: abbr,
      speciesName: selectedSpeciesName,
      seasonGroupName: selectedSeasonGroupName,
      techniqueName: selectedTechniqueName,
    });
  }, [selectedSpeciesName, selectedSeasonGroupName, abbr, selectedTechniqueName]);

  useEffect(() => {
    let ignore = false;
    const fetchStateTechniques = async () => {
      const response = await apiV1.stateTechniquesFetch(slug, {
        season_group: selectedSeasonGroupName,
        species: selectedSpeciesName,
      });
      if (!ignore) {
        setTopTechniqueNamesCounts(response.data.data);
      }
    };
    fetchStateTechniques();
    apiV1
      .stateTopWaterbodiesFetch(slug, {
        limit: 20,
        species: selectedSpeciesName,
        season_group: selectedSeasonGroupName,
      })
      .then((response) => {
        if (!ignore) {
          setSeasonSpeciesTopWaterbodies(response.data.data);
        }
      });
    apiV1
      .stateFishingReportCountsFetch(slug, {
        species: selectedSpeciesName,
        season_group: selectedSeasonGroupName,
      })
      .then((res) => {
        if (!ignore) {
          setFishingReportsCount(res.data.data.all_time);
          setFishingReports30DaysCount(res.data.data.thirty_days);
        }
      });

    return () => {
      ignore = true;
    };
  }, [selectedSeasonGroupName, selectedSpeciesName]);

  useEffect(() => {
    let ignore = false;
    const params: Record<string, string> = {};
    if (!selectedSeasonGroupName && !selectedSpeciesName) {
      return;
    }
    if (selectedSeasonGroupName) {
      params.season_group = selectedSeasonGroupName;
    }
    if (selectedSpeciesName) {
      params.species = selectedSpeciesName;
    }
    if (selectedTechniqueName) {
      params.style = selectedTechniqueName;
    }
    const fetchStateBestSellers = async () => {
      setProductsLoadingState(LoadingState.PENDING);
      const response = await apiV1.stateBestSellersFetch(slug, params);
      if (!ignore) {
        setHotbaits(response.data.data.hotbaits);
        setBestSellers(response.data.data.best_sellers);
        setRecommendations(response.data.data.recommendations);
        setProductsLoadingState(LoadingState.DONE);
      }
      const hotbaitsSorted = _.orderBy(
        response.data.data.hotbaits,
        [
          (sh) => sh.fishing_reports.length,
          (sh) => sh.product.state_fishing_report_counters[stateDetail.abbr],
        ],
        ['desc', 'desc']
      );

      const allProducts = [
        ...hotbaitsSorted.map((sh) => sh.product),
        ...response.data.data.best_sellers.map((bs) => bs.product),
        ...response.data.data.recommendations,
      ];
      const productsByCategory = _.groupBy(allProducts, (p) => p.category_name);
      const products: Product[] = [];
      Object.entries(productsByCategory).map(([key, values]) => {
        if (kitCategoryNames.includes(key as CategoryNames)) {
          products.push(...values.slice(0, 1));
        }
      });
      const productsOrdered = _.orderBy(
        products,
        (p) => _.indexOf(kitCategoryNames, p.category_name),
        ['asc']
      );
      if (!ignore) {
        setKitPreviewProducts(productsOrdered);
      }
    };
    fetchStateBestSellers();

    return () => {
      ignore = true;
    };
  }, [selectedSpeciesName, selectedSeasonGroupName, selectedTechniqueName]);

  useEffect(() => {
    setSelectedSeasonGroupName(determinedSeasonGroupName);
    setSelectedSpeciesName(determinedSpeciesName);
    setSelectedTechniqueName(queryParamTechniqueName);
  }, [location.search]);

  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [hotbaits, setHotbaits] = useState<StateHotbait[]>([]);
  const [bestSellers, setBestSellers] = useState<BestSeller[]>([]);
  const [recommendations, setRecommendations] = useState([] as Product[]);
  const [productsLoadingState, setProductsLoadingState] = useState(LoadingState.NOT_STARTED);
  const [kitPreviewProducts, setKitPreviewProducts] = useState([] as Product[]);

  const seasonSpeciesSelectsRef = useRef(null);

  const stateName = _.startCase(name);
  const metaTitle = meta_title || stateName;
  const hasLearnMoreSection = !!qas.length;

  const STATE_DETAIL_HEADER_HEIGHT_APPROX = isMobile ? 132 : 195;

  const scrollToRef = (currentRef: RefObject<HTMLElement> | void) => {
    if (currentRef && currentRef.current) {
      scrollToElementTop(
        currentRef.current,
        STATE_DETAIL_HEADER_HEIGHT_APPROX + WATERBODY_DETAIL_NAV_HEIGHT_APPROX
      );
    }
  };

  const scrollToProducts = () => {
    scrollToElementTop(productsRef.current, isMobile ? 162 : 154);
  };

  const [applySelectsAnimation, setApplySelectsAnimation] = useState(false);
  const scrollToSelectsSection = useCallback(
    (blink?: boolean) => {
      scrollToRef(seasonSpeciesSelectsRef);
      if (blink) {
        setTimeout(() => setApplySelectsAnimation(true), 600);
      }
    },
    [seasonSpeciesSelectsRef.current]
  );

  const isDesktopSticky = isDesktop && isHeaderSticky;
  const isMobileSticky = isMobile && isHeaderSticky;
  const bestSellerProducts = useMemo(() => {
    return bestSellers.map((bs) => bs.product);
  }, [bestSellers]);

  return (
    <section>
      <SEO titleTemplate="%s" title={metaTitle} description={meta_description} />
      <section>
        <div className={styles.headerContainer} id={StatePageIds.overview}>
          <Affix onChange={(affixed) => setIsHeaderSticky(affixed)}>
            <div className={styles.headerBackground}>
              <ContentWrapper>
                <div
                  className={classNames(styles.detailsPageHeader, {
                    [styles.detailsPageHeader__sticky]: isHeaderSticky,
                  })}
                >
                  <div className={styles.headerContentsWrapper}>
                    <div>
                      <h1
                        className={classNames(styles.lakeName, {
                          [styles.lakeName__sticky]: isHeaderSticky,
                        })}
                      >
                        {stateName}
                      </h1>
                      {isDesktopSticky && (
                        <SeasonSpeciesHeaderTag
                          isSticky={isHeaderSticky}
                          selectedSeasonGroup={selectedSeasonGroup}
                          selectedSpecies={selectedSpecies}
                          scrollToSelectsSection={() => scrollToSelectsSection(true)}
                        />
                      )}
                    </div>
                    <FishingReportButton
                      fishingReportsCount={fishingReportsCount}
                      isHeaderSticky={isHeaderSticky}
                    />
                  </div>
                  {isMobileSticky && (
                    <>
                      <Spacer size={10} />
                      <SeasonSpeciesHeaderTag
                        isSticky={isHeaderSticky}
                        selectedSeasonGroup={selectedSeasonGroup}
                        selectedSpecies={selectedSpecies}
                        scrollToSelectsSection={() => scrollToSelectsSection(true)}
                      />
                    </>
                  )}
                </div>
              </ContentWrapper>
              <StateDetailNav mediaCount={media.length} hasLearnMoreSection={hasLearnMoreSection} />
            </div>
          </Affix>
        </div>
      </section>
      <section>
        <div className={styles.upperPageBackground}>
          <ContentWrapper>
            <h2 className={styles.topLakesHeader} id={StatePageIds.top_lakes}>
              Top {stateName} Lakes
            </h2>
            <p className={styles.speciesSeasonTag}>
              Based on Fishing Reports for {selectedSeasonGroup.display_name}{' '}
              {selectedSpecies.display_name}
            </p>
            <section ref={seasonSpeciesSelectsRef} className={styles.selectsSection}>
              <SeasonSpeciesSelects
                applySelectsAnimation={applySelectsAnimation}
                onApplySelectsAnimation={() => setApplySelectsAnimation(false)}
                selectedSpecies={selectedSpecies}
                selectedSeasonGroup={selectedSeasonGroup}
                seasonGroups={season_groups}
                currentSeasonGroup={currentSeasonGroup}
                speciesOptions={isIceSeason ? iceFilteredSpecies : stateSpecies}
                className={styles.selects}
              />
            </section>
            <section className={styles.topWaterbodiesSection}>
              <TopWaterbodies
                topWaterbodies={seasonSpeciesTopWaterbodies}
                bbox={bbox}
                selectedSpeciesName={selectedSpeciesName}
                selectedSeasonGroupName={selectedSeasonGroupName}
              />
            </section>
          </ContentWrapper>
          <div className={styles.topTechniquesBackground}>
            <ContentWrapper>
              <section id={StatePageIds.stats} className={styles.stats}>
                <h2>{stateDetail.name} Stats</h2>
                <p className={styles.speciesSeasonTag}>
                  Based on Fishing Reports for {selectedSeasonGroup.display_name}{' '}
                  {selectedSpecies.display_name}
                </p>
                <div className={styles.statsWrapper}>
                  <StateStats
                    key={selectedSeasonGroupName + selectedSpeciesName}
                    onTechniqueClick={(t: TechniqueFullWithReportCount) => {
                      replaceQueryString({ [OmniaQueryParams.technique]: t.name });
                      scrollToProducts();
                    }}
                    selectedSpecies={selectedSpecies}
                    selectedSeasonGroup={selectedSeasonGroup}
                    state={stateDetail}
                    topTechniquesWithCounts={orderedTopTechniquesWithCounts}
                    kitPreviewProducts={kitPreviewProducts}
                    topWaterbodies={seasonSpeciesTopWaterbodies}
                    fishingReports30DaysCount={fishingReports30DaysCount}
                    fishingReportsCount={fishingReportsCount}
                  />
                </div>
              </section>
            </ContentWrapper>
          </div>
        </div>
        <section>
          <div className={styles.popularProductsBackground}>
            <ContentWrapper>
              <h2 className={styles.popularProductsHeading} ref={productsRef}>
                Popular Products
              </h2>
              <p className={styles.speciesSeasonTag} id={StatePageIds.popular}>
                Reported in {stateName} for {selectedSeasonGroup.display_name}{' '}
                {selectedSpecies.display_name}
              </p>
              <div className={styles.popularProductsWrapper}>
                <TechniqueOverflowMenu
                  topTechniques={orderedTopTechniquesWithCounts}
                  selectedTechnique={techniqueByTechniqueName(selectedTechniqueName)}
                  selectedSeasonGroup={selectedSeasonGroup}
                  selectedSpecies={selectedSpecies}
                  context={'state'}
                />
                <div className={styles.cardContainer}>
                  {isPending(productsLoadingState) ? (
                    <Loading text="Loading Popular Products" />
                  ) : (
                    <CategoryProductsCarousels
                      hotbaits={hotbaits}
                      recommendations={recommendations}
                      bestSellers={bestSellerProducts}
                      stateAbbr={abbr}
                      onTechniqueClick={() => {
                        // query string is set in the products_group_card
                        scrollToProducts();
                      }}
                      selectedTechniqueName={selectedTechniqueName}
                    />
                  )}
                </div>
              </div>
            </ContentWrapper>
          </div>
        </section>

        <ContentWrapper>
          {!!media.length && (
            <>
              <section className={styles.section}>
                <h2
                  id={StatePageIds.videos_articles}
                  className={styles.h3}
                >{`Videos & Articles (${media.length})`}</h2>
                <div className={styles.carousel}>
                  <OmniaCarousel
                    key={media.map((content) => content.uid).join()}
                    arrows={true}
                    fadedEdges
                  >
                    {media.map((content) => {
                      return (
                        <div className={styles.content} key={content.uid}>
                          <MediaCardLegacy media={content} imgWidth={427} />
                        </div>
                      );
                    })}
                  </OmniaCarousel>
                </div>
              </section>
            </>
          )}

          {hasLearnMoreSection && (
            <>
              <section className={styles.section}>
                <h2 className={styles.h3} id={StatePageIds.questions}>
                  Learn More about {name} Fishing
                </h2>
                <QuestionsCollapse qas={qas} />
              </section>
            </>
          )}
        </ContentWrapper>
      </section>
    </section>
  );
};
